// ============================================
// pages/unloading/UnloadingEntryForm.tsx — Create/edit one unloading batch
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Send, ArrowLeft, Loader2, CheckCircle, XCircle, PackageOpen, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  seasonsAPI, districtsAPI, commoditiesAPI, godownsAPI, unloadingContractorsAPI, unloadingEntriesAPI,
} from '../../api/services';
import { UserRole, formatAmount, num } from '../../types/markfed';
import type { Season, District } from '../../types/markfed';

const empty = {
  commodity_id: 0, district_id: 0, godown_id: 0, contractor_id: 0,
  entry_id_ext: '', entry_date: '', bill_no: '', bill_date: '',
  bags_unloaded: 0, weight_qtl: 0, rate_per_bag: 0,
  amount_verified: 0, remarks: '',
};

export const UnloadingEntryForm: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isCreate = !idParam || idParam === 'new';
  const recordId = isCreate ? null : parseInt(idParam);
  const isDM = user?.role === UserRole.DM;

  const [season, setSeason] = useState<Season | null>(null);
  const [districts, setDistricts] = useState<District[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [godowns, setGodowns] = useState<any[]>([]);
  const [contractors, setContractors] = useState<any[]>([]);
  const [form, setForm] = useState<any>(empty);
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const canEdit = isCreate || status === 'draft' || status === 'rejected';

  const amountClaimed = useMemo(
    () => num(form.bags_unloaded) * num(form.rate_per_bag),
    [form.bags_unloaded, form.rate_per_bag],
  );

  useEffect(() => {
    (async () => {
      try {
        const [sRes, dRes, cRes, ctRes] = await Promise.all([
          seasonsAPI.active(), districtsAPI.list(), commoditiesAPI.list(), unloadingContractorsAPI.list(),
        ]);
        setSeason(sRes.data);
        setDistricts(dRes.data);
        setCommodities(cRes.data);
        setContractors(ctRes.data);
        if (isCreate && isDM && user?.district_id) {
          setForm((f: any) => ({ ...f, district_id: user.district_id }));
        }
        if (!isCreate && recordId && sRes.data) {
          const res = await unloadingEntriesAPI.get(sRes.data.id, recordId);
          const raw: any = res.data;
          setForm({
            commodity_id: num(raw.commodity_id),
            district_id: num(raw.district_id),
            godown_id: num(raw.godown_id),
            contractor_id: num(raw.contractor_id),
            entry_id_ext: raw.entry_id_ext || '',
            entry_date: raw.entry_date ? raw.entry_date.split('T')[0] : '',
            bill_no: raw.bill_no || '',
            bill_date: raw.bill_date ? raw.bill_date.split('T')[0] : '',
            bags_unloaded: num(raw.bags_unloaded),
            weight_qtl: num(raw.weight_qtl),
            rate_per_bag: num(raw.rate_per_bag),
            amount_verified: num(raw.amount_verified),
            remarks: raw.remarks || '',
          });
          setStatus(raw.status || 'draft');
        }
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, recordId]);

  useEffect(() => {
    if (!form.district_id) { setGodowns([]); return; }
    godownsAPI.list(form.district_id).then((r) => setGodowns(r.data)).catch(() => setGodowns([]));
  }, [form.district_id]);

  const handleChange = (field: string, value: any) => setForm((f: any) => ({ ...f, [field]: value }));

  const handleSave = async (submit = false) => {
    if (!season) return;
    if (!form.commodity_id || !form.district_id || !form.godown_id || !form.contractor_id) {
      setMessage({ type: 'error', text: 'Commodity, district, godown and contractor are required' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const payload = { ...form, amount_claimed: amountClaimed };
      if (isCreate) {
        const res = await unloadingEntriesAPI.create(season.id, payload);
        const newId = (res.data as any)?.id;
        if (submit && newId) {
          await unloadingEntriesAPI.submit(season.id, newId);
          setStatus('submitted');
        }
        setMessage({ type: 'success', text: submit ? 'Entry created and submitted' : 'Entry created as draft' });
        if (newId) navigate(`/unloading/entries/${newId}`, { replace: true });
      } else if (recordId) {
        await unloadingEntriesAPI.update(season.id, recordId, payload);
        if (submit) {
          await unloadingEntriesAPI.submit(season.id, recordId);
          setStatus('submitted');
        }
        setMessage({ type: 'success', text: submit ? 'Entry submitted' : 'Draft saved' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        <span className="ml-2 text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={() => navigate('/unloading/entries')}
        className="mb-4 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
        <ArrowLeft className="w-4 h-4" /> Back to Entries
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <PackageOpen className="w-7 h-7 text-blue-600" />
          {isCreate ? 'New Unloading Entry' : 'Unloading Entry'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Status: <span className="font-semibold uppercase">{status}</span>
          {season && <span className="text-blue-600 font-medium"> | {season.season_name}</span>}
        </p>
      </div>

      {!canEdit && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
          <AlertCircle className="w-4 h-4" />
          This entry is {status}. Fields are read-only.
        </div>
      )}

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Commodity *</label>
            <select value={form.commodity_id} onChange={(e) => handleChange('commodity_id', parseInt(e.target.value))}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {commodities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">District *</label>
            {isDM ? (
              <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm font-medium text-green-700">
                {districts.find((d) => d.id === form.district_id)?.name || `District #${form.district_id}`}
              </div>
            ) : (
              <select value={form.district_id} onChange={(e) => handleChange('district_id', parseInt(e.target.value))}
                disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
                <option value={0}>-- Select --</option>
                {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Godown *</label>
            <select value={form.godown_id} onChange={(e) => handleChange('godown_id', parseInt(e.target.value))}
              disabled={!canEdit || !form.district_id} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>{form.district_id ? '-- Select Godown --' : 'Select district first'}</option>
              {godowns.map((g: any) => <option key={g.id} value={g.id}>{g.godown_name} ({g.godown_type})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Contractor *</label>
            <select value={form.contractor_id} onChange={(e) => handleChange('contractor_id', parseInt(e.target.value))}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {contractors.map((c: any) => (
                <option key={c.id} value={c.id}>{c.firm_name || c.contractor_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Entry ID</label>
            <input type="text" value={form.entry_id_ext} onChange={(e) => handleChange('entry_id_ext', e.target.value)}
              disabled={!canEdit} placeholder="e.g. UE-001"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Entry Date</label>
            <input type="date" value={form.entry_date} onChange={(e) => handleChange('entry_date', e.target.value)}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Bags Unloaded</label>
            <input type="number" min={0} value={form.bags_unloaded || ''}
              onChange={(e) => handleChange('bags_unloaded', parseInt(e.target.value) || 0)}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Weight (Qtl)</label>
            <input type="number" step="0.01" min={0} value={form.weight_qtl || ''}
              onChange={(e) => handleChange('weight_qtl', parseFloat(e.target.value) || 0)}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Rate per Bag (Rs.)</label>
            <input type="number" step="0.01" min={0} value={form.rate_per_bag || ''}
              onChange={(e) => handleChange('rate_per_bag', parseFloat(e.target.value) || 0)}
              disabled={!canEdit} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
            <textarea value={form.remarks} onChange={(e) => handleChange('remarks', e.target.value)}
              disabled={!canEdit} rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
        </div>

        <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-200 grid grid-cols-3 gap-3 text-center">
          <div><p className="text-xs text-gray-500">Amount Claimed</p><p className="text-base font-bold text-blue-600">{formatAmount(amountClaimed)}</p></div>
          <div><p className="text-xs text-gray-500">Amount Verified</p><p className="text-base font-bold text-green-700">{form.amount_verified > 0 ? formatAmount(form.amount_verified) : '—'}</p></div>
          <div><p className="text-xs text-gray-500">Bags × Rate</p><p className="text-[10px] text-gray-500 mt-1">{num(form.bags_unloaded)} × {num(form.rate_per_bag)}</p></div>
        </div>

        {canEdit && (
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => handleSave(false)} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-lg disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Draft
            </button>
            <button onClick={() => handleSave(true)} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnloadingEntryForm;
